import { useEffect, useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard, Search, ShieldCheck, Radio, Settings, Moon, Sun,
  SlidersHorizontal, KeyRound, Bell, FileText, Menu, X, Compass, LogOut,
  Users, MessageSquare, Briefcase, Terminal, CreditCard,
} from 'lucide-react'
import clsx from 'clsx'
import type { Me } from '../lib/api'
import { LanguageSwitcher } from './LanguageSwitcher'
import { signOut } from '../lib/auth'

// S-UIX-19 (UIUX-REVIEW §4): every nav item names the roles that may see it;
// the server still enforces authorization — this is display gating only.
const TENANT_ADMIN = ['TENANT_ADMIN', 'PLATFORM_ADMIN']
const INTEGRATION_ROLES = ['TENANT_ADMIN', 'PLATFORM_ADMIN', 'API_CLIENT']

const NAV = [
  { to: '/', key: 'nav.dashboard', icon: LayoutDashboard, end: true, roles: null as string[] | null },
  { to: '/discover', key: 'nav.discover', icon: Compass, end: false, roles: null },
  { to: '/search', key: 'nav.search', icon: Search, roles: null },
  { to: '/verify', key: 'nav.verify', icon: ShieldCheck, roles: null },
  { to: '/signals', key: 'nav.signals', icon: Radio, roles: null },
  { to: '/watchlists', key: 'nav.watchlists', icon: Bell, roles: null },
  { to: '/cases', key: 'nav.cases', icon: Briefcase, roles: null },
  { to: '/webhooks', key: 'nav.webhooks', icon: FileText, roles: INTEGRATION_ROLES },
  { to: '/channels', key: 'nav.channels', icon: MessageSquare, roles: TENANT_ADMIN },
  { to: '/team', key: 'nav.team', icon: Users, roles: TENANT_ADMIN },
  { to: '/settings/keys', key: 'nav.keys', icon: KeyRound, roles: INTEGRATION_ROLES },
  { to: '/playground', key: 'nav.playground', icon: Terminal, roles: null },
  { to: '/pricing', key: 'nav.pricing', icon: CreditCard, roles: null },
  { to: '/settings', key: 'nav.settings', icon: Settings, roles: null },
  { to: '/admin', key: 'nav.admin', icon: SlidersHorizontal, roles: ['PLATFORM_ADMIN'] },
]

function NavLinks({ roles, onNavigate }: { roles: string[]; onNavigate?: () => void }) {
  const { t } = useTranslation()
  return (
    <nav className="space-y-1" aria-label="Main">
      {NAV.filter(({ roles: allowed }) => !allowed || allowed.some((r) => roles.includes(r))).map(({ to, key, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            clsx(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all',
              isActive
                ? 'bg-gradient-to-r from-brand-600/15 to-brand-600/5 text-brand-700 shadow-sm dark:text-brand-100'
                : 'text-[var(--text-muted)] hover:bg-brand-600/5 hover:text-[var(--text-primary)]',
            )
          }
        >
          <Icon className="h-4 w-4" aria-hidden />
          {t(key)}
        </NavLink>
      ))}
    </nav>
  )
}

function Brand() {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-brand-600 to-brand-900 shadow-md shadow-brand-600/20">
        <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 text-white" aria-hidden>
          <path d="M12 2L4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3z" stroke="currentColor" strokeWidth="1.6" />
          <path d="M8.5 12l2.5 2.5L16 9.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div>
        <div className="text-sm font-bold tracking-tight">{t('app.name')}</div>
        <div className="text-[10px] text-[var(--text-muted)]">{t('app.tagline')}</div>
      </div>
    </div>
  )
}

export function AppShell({ children, me }: { children: React.ReactNode; me: Me | null }) {
  const roles = me?.roles ?? ['ANALYST']
  const { t } = useTranslation()
  const [dark, setDark] = useState(() => localStorage.getItem('vb-theme') === 'dark')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [announcement, setAnnouncement] = useState<string | null>(null)

  useEffect(() => {
    fetch('/v1/meta/announcements')
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((rows: { message: string }[]) => {
        if (rows.length > 0) setAnnouncement(rows[0].message)
      })
      .catch(() => {})
  }, [])

  function toggleTheme() {
    const next = !dark
    setDark(next)
    localStorage.setItem('vb-theme', next ? 'dark' : 'light')
    document.documentElement.classList.toggle('dark', next)
  }

  // REQ-021 (US-010): identity + Sign out render only for Keycloak sessions
  // (me.email present). The dev-only api-key fallback has no realm session to end.
  const email = me?.email ?? ''

  function SessionControls({ stacked = false }: { stacked?: boolean }) {
    if (!email) return null
    return (
      <div className={clsx('flex items-center gap-2', stacked && 'w-full flex-col items-stretch gap-1 border-t border-[var(--border)] pt-3')}>
        <span
          className={clsx('truncate text-xs text-[var(--text-muted)]', stacked ? 'px-3 py-1' : 'hidden max-w-52 sm:inline')}
          title={email}
        >
          {email}
        </span>
        <button
          onClick={() => void signOut()}
          aria-label={t('nav.signout')}
          className={clsx(
            'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-[var(--text-muted)] transition-colors hover:bg-brand-600/5 hover:text-[var(--text-primary)]',
            stacked && 'justify-start',
          )}
        >
          <LogOut className="h-4 w-4" aria-hidden />
          {t('nav.signout')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-brand-600 focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to content
      </a>
      {announcement && (
        <div
          role="status"
          className="flex items-center justify-between gap-3 bg-gradient-to-r from-brand-700 to-brand-600 px-4 py-2 text-sm text-white sm:px-6"
        >
          <span>{announcement}</span>
          <button className="text-white/80 hover:text-white" onClick={() => setAnnouncement(null)} aria-label="dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        {/* desktop sidebar */}
        <aside className="hidden w-60 shrink-0 flex-col border-r border-[var(--border)] bg-[var(--surface-card)] md:flex">
          <div className="px-5 py-5">
            <Brand />
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-2">
            <NavLinks roles={roles} />
          </div>
          <div className="px-5 py-4 text-[10px] text-[var(--text-muted)]">
            Vbanna Pte Ltd · Singapore
          </div>
        </aside>

        {/* main column */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-20 flex items-center justify-between border-b border-[var(--border)] bg-[var(--surface-card)]/90 px-4 py-3 backdrop-blur sm:px-6">
            <div className="flex items-center gap-2 md:hidden">
              <button
                className="rounded-lg p-2 hover:bg-brand-600/5"
                onClick={() => setDrawerOpen(true)}
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <span className="font-semibold">{t('app.name')}</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <SessionControls />
              <LanguageSwitcher />
              <button
                onClick={toggleTheme}
                aria-label={t('nav.theme')}
                className="rounded-lg p-2 text-[var(--text-muted)] transition-colors hover:bg-brand-600/5 hover:text-[var(--text-primary)]"
              >
                {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
            </div>
          </header>

          {/* mobile drawer */}
          {drawerOpen && (
            <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true">
              <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
              <aside className="absolute inset-y-0 left-0 flex w-64 flex-col border-r border-[var(--border)] bg-[var(--surface-card)] shadow-xl">
                <div className="flex items-center justify-between px-4 py-4">
                  <Brand />
                  <button onClick={() => setDrawerOpen(false)} aria-label="Close menu">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="px-3" onClick={() => setDrawerOpen(false)}>
                  <NavLinks roles={roles} />
                </div>
                <div className="mt-auto px-4 pb-4" onClick={() => setDrawerOpen(false)}>
                  <SessionControls stacked />
                </div>
              </aside>
            </div>
          )}

          <main id="main" className="flex-1 overflow-y-auto p-4 sm:p-6">{children}</main>

          <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--border)] px-6 py-3 text-xs text-[var(--text-muted)]">
            <span>Vbanna Pte Ltd · Singapore</span>
            <div className="flex gap-4">
              <Link className="hover:text-[var(--text-primary)]" to="/terms">Terms</Link>
              <Link className="hover:text-[var(--text-primary)]" to="/privacy">Privacy</Link>
              <a href="/v1/meta/status" target="_blank" rel="noreferrer" className="hover:text-[var(--text-primary)]">
                Status
              </a>
            </div>
          </footer>
        </div>
      </div>
    </div>
  )
}
